import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaArrowRight, FaIndustry, FaWhatsapp, FaEnvelope, FaCheckCircle } from 'react-icons/fa';

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [whatsapp, setWhatsapp] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(`/api/products/${slug}`)
      .then(r => { setProduct(r.data); setLoading(false); })
      .catch(() => { setLoading(false); navigate('/urunler'); });
    axios.get('/api/settings').then(r => setWhatsapp(r.data.whatsapp_number || '')).catch(() => {});
  }, [slug, navigate]);

  if (loading) return (
    <div className="pt-20 min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!product) return null;

  const allImages = [
    ...(product.main_image ? [{ image_url: product.main_image }] : []),
    ...(product.images || [])
  ];

  const specs = product.specifications && typeof product.specifications === 'object'
    ? (Array.isArray(product.specifications) ? product.specifications : Object.entries(product.specifications).map(([k, v]) => ({ key: k, value: v })))
    : [];

  return (
    <div className="pt-20">
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="text-sm text-gray-500 flex items-center gap-2">
            <Link to="/" className="hover:text-secondary">Ana Sayfa</Link>
            <FaArrowRight size={10} className="text-gray-300" />
            <Link to="/urunler" className="hover:text-secondary">Ürünler</Link>
            {product.category_name && <>
              <FaArrowRight size={10} className="text-gray-300" />
              <Link to={`/urunler?kategori=${product.category_slug}`} className="hover:text-secondary">{product.category_name}</Link>
            </>}
            <FaArrowRight size={10} className="text-gray-300" />
            <span className="text-gray-700 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Images */}
          <div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 mb-4 relative">
              {allImages.length > 0 ? (
                <img src={allImages[activeImg]?.image_url} alt={product.name}
                  className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <FaIndustry size={80} />
                </div>
              )}
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {allImages.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${i === activeImg ? 'border-secondary' : 'border-transparent'}`}>
                    <img src={img.image_url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {product.category_name && (
              <Link to={`/urunler?kategori=${product.category_slug}`}
                className="badge bg-secondary/10 text-secondary mb-3 inline-block hover:bg-secondary hover:text-white transition-colors">
                {product.category_name}
              </Link>
            )}
            <h1 className="text-3xl font-bold text-primary mb-4">{product.name}</h1>
            {product.short_description && (
              <p className="text-gray-600 text-lg leading-relaxed mb-6 border-l-4 border-secondary pl-4">
                {product.short_description}
              </p>
            )}

            {/* Specs */}
            {specs.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 mb-3">Teknik Özellikler</h3>
                <div className="bg-gray-50 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody>
                      {specs.map((s, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2.5 font-medium text-gray-700 w-2/5">{s.key}</td>
                          <td className="px-4 py-2.5 text-gray-600">{s.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Link to="/iletisim" className="btn-primary flex-1 justify-center">
                <FaEnvelope /> Bilgi Al
              </Link>
              {whatsapp && (
                <a href={`https://wa.me/${whatsapp}?text=Merhaba, ${product.name} hakkında bilgi almak istiyorum.`}
                  target="_blank" rel="noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
                  <FaWhatsapp size={18} /> WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Full Description */}
        {product.full_description && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-primary mb-6 pb-3 border-b-2 border-secondary inline-block">Ürün Açıklaması</h2>
            <div className="prose max-w-none text-gray-600 leading-relaxed whitespace-pre-line">
              {product.full_description}
            </div>
          </div>
        )}

        {/* Related */}
        {product.related?.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-primary mb-6">İlgili Ürünler</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {product.related.map(p => (
                <Link key={p.id} to={`/urunler/${p.slug}`} className="card group">
                  <div className="h-40 img-placeholder overflow-hidden">
                    {p.main_image ? (
                      <img src={p.main_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <FaIndustry size={32} />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="font-medium text-primary text-sm group-hover:text-secondary transition-colors line-clamp-2">{p.name}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
